// ─────────────────────────────────────────────────────────────────────────────
// Query Parser — Rule-based natural language command parser
// Parses user queries into structured intent objects without any external AI API
// Used by: smartCommandEngine.js → AIAssistantDrawer.jsx
// ─────────────────────────────────────────────────────────────────────────────

// ── Keyword dictionaries ────────────────────────────────────────────────────

const INTENT_KEYWORDS = {
  invoice_filter: [
    'pending', 'overdue', 'unpaid', 'remaining', 'outstanding', 'due',
    'invoice', 'invoices', 'billing', 'payment pending',
  ],
  revenue: [
    'revenue', 'income', 'earning', 'earnings', 'collection', 'collections',
    'total paid', 'paid amount', 'money', 'profit',
  ],
  patient: [
    'patient', 'patients', 'member', 'members', 'inactive', 'active',
    'new patient', 'registration', 'registrations',
  ],
  appointment: [
    'appointment', 'appointments', 'session', 'sessions', 'booking',
    'bookings', 'schedule', 'scheduled', 'slot', 'slots',
  ],
  therapist: [
    'therapist', 'doctor', 'staff', 'employee', 'top therapist',
    'best doctor', 'top doctor', 'top performer',
  ],
  therapy: [
    'speech therapy', 'occupational therapy', 'physiotherapy',
    'special education', 'aba therapy', 'art therapy',
    'art & craft therapy', 'art and craft',
  ],
  expense: [
    'expense', 'expenses', 'spending', 'cost', 'costs', 'expenditure',
  ],
  report: [
    'report', 'monthly report', 'summary', 'analytics', 'analysis',
    'overview', 'generate report', 'insight', 'insights',
  ],
  trend: [
    'trend', 'trends', 'growth', 'decline', 'prediction', 'forecast',
    'growing', 'declining', 'stable',
  ],
  cancellation: [
    'cancel', 'cancellation', 'cancelled', 'canceled', 'no show',
    'no-show', 'missed',
  ],
  attendance: [
    'attendance', 'absent', 'present', 'absent staff',
    'patient attendance', 'mark attendance', 'centre off',
  ],
  salary: [
    'salary', 'salaries', 'payroll', 'payout', 'pending salary',
  ],
  waiting_list: [
    'waiting list', 'waiting', 'queue', 'waitlist', 'wait list',
  ],
  services: [
    'service', 'services', 'therapy service', 'service list',
  ],
  session_types: [
    'session type', 'session types', 'session category',
  ],
  pricing: [
    'pricing', 'pricing plan', 'pricing plans', 'plan', 'plans',
    'subscription', 'billing plan',
  ],
  settlement: [
    'settlement', 'monthly settlement', 'generate invoice',
    'month end', 'billing settlement',
  ],
  predictive: [
    'predictive', 'prediction', 'dropout', 'dropout risk',
    'revenue forecast', 'business advisor', 'advisor',
    'intelligence', 'insights', 'ai insights',
  ],
  clinical: [
    'clinical', 'progress', 'stagnation', 'therapy effectiveness',
    'goal achievement', 'clinical intelligence', 'therapy intelligence',
  ],
  designation: [
    'designation', 'designations', 'staff role',
  ],
  support: [
    'help', 'support', 'faq', 'ticket', 'support ticket', 'contact',
  ],
  referrals: [
    'referral', 'referrals', 'referred', 'referrer', 'referred by', 'referred patients',
  ],
  wallet_credits: [
    'wallet', 'wallet credit', 'credits',
  ],
  guests: [
    'guest', 'guests', 'guest session', 'not a patient',
  ],
  group_sessions: [
    'group', 'group session', 'group sessions', 'group day',
  ],
  leave: [
    'leave', 'leave request', 'leave balance', 'leave management', 'leave type',
  ],
  roles: [
    'role', 'roles', 'permission', 'permissions', 'roles & permissions', 'roles and permissions',
  ],
};

const THERAPY_MAP = {
  'speech': 'Speech Therapy',
  'speech therapy': 'Speech Therapy',
  'occupational': 'Occupational Therapy',
  'occupational therapy': 'Occupational Therapy',
  'ot': 'Occupational Therapy',
  'physio': 'Physiotherapy',
  'physiotherapy': 'Physiotherapy',
  'special education': 'Special Education',
  'special ed': 'Special Education',
  'aba': 'ABA Therapy',
  'aba therapy': 'ABA Therapy',
  'art': 'Art & Craft Therapy',
  'art therapy': 'Art & Craft Therapy',
  'art & craft': 'Art & Craft Therapy',
  'art and craft': 'Art & Craft Therapy',
};

const TIME_KEYWORDS = {
  'today': 'today',
  'yesterday': 'yesterday',
  'this week': 'this_week',
  'last week': 'last_week',
  'this month': 'this_month',
  'last month': 'last_month',
  'this year': 'this_year',
  'last year': 'last_year',
  'january': 'january', 'february': 'february', 'march': 'march',
  'april': 'april', 'may': 'may', 'june': 'june',
  'july': 'july', 'august': 'august', 'september': 'september',
  'october': 'october', 'november': 'november', 'december': 'december',
  'jan': 'january', 'feb': 'february', 'mar': 'march',
  'apr': 'april', 'jun': 'june', 'jul': 'july',
  'aug': 'august', 'sep': 'september', 'oct': 'october',
  'nov': 'november', 'dec': 'december',
};

const COMPARATOR_PATTERNS = [
  { regex: /(?:above|over|greater than|more than|>|exceeding)\s*(\d+(?:,\d{3})*)/i, type: 'gt' },
  { regex: /(?:below|under|less than|lower than|<)\s*(\d+(?:,\d{3})*)/i, type: 'lt' },
  { regex: /(?:equal to|exactly|=)\s*(\d+(?:,\d{3})*)/i, type: 'eq' },
  { regex: /(?:between)\s*(\d+(?:,\d{3})*)\s*(?:and|to|-)\s*(\d+(?:,\d{3})*)/i, type: 'between' },
];

// ── Core parser ─────────────────────────────────────────────────────────────

const parseNumber = (str) => parseInt(str.replace(/,/g, ''), 10);

const extractNumbers = (query) => {
  const matches = query.match(/\d+(?:,\d{3})*/g);
  return matches ? matches.map(parseNumber) : [];
};

const extractTimeframe = (query) => {
  const lower = query.toLowerCase();
  for (const [keyword, value] of Object.entries(TIME_KEYWORDS)) {
    if (lower.includes(keyword)) return value;
  }
  return null;
};

const extractTherapy = (query) => {
  const lower = query.toLowerCase();
  for (const [keyword, value] of Object.entries(THERAPY_MAP)) {
    if (lower.includes(keyword)) return value;
  }
  return null;
};

const extractComparator = (query) => {
  for (const { regex, type } of COMPARATOR_PATTERNS) {
    const match = query.match(regex);
    if (match) {
      if (type === 'between') {
        return { type, min: parseNumber(match[1]), max: parseNumber(match[2]) };
      }
      return { type, value: parseNumber(match[1]) };
    }
  }
  return null;
};

const detectIntent = (query) => {
  const lower = query.toLowerCase();
  const scores = {};

  for (const [intent, keywords] of Object.entries(INTENT_KEYWORDS)) {
    let score = 0;
    for (const keyword of keywords) {
      if (lower.includes(keyword)) {
        // Longer keyword matches get higher score
        score += keyword.split(' ').length;
      }
    }
    if (score > 0) scores[intent] = score;
  }

  // Sort by score descending, return top intent
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  return sorted.length > 0 ? { intent: sorted[0][0], score: sorted[0][1], all: scores } : null;
};

const detectModifiers = (query) => {
  const lower = query.toLowerCase();
  const modifiers = {};

  if (/\btop\b/i.test(lower)) modifiers.sort = 'top';
  if (/\bbottom\b|\blowest\b|\bworst\b/i.test(lower)) modifiers.sort = 'bottom';
  if (/\binactive\b/i.test(lower)) modifiers.status = 'inactive';
  if (/\bactive\b/i.test(lower)) modifiers.status = 'active';
  if (/\bnew\b/i.test(lower)) modifiers.status = 'new';
  if (/\bpending\b/i.test(lower)) modifiers.paymentStatus = 'pending';
  if (/\bpaid\b/i.test(lower)) modifiers.paymentStatus = 'paid';
  if (/\bpartial\b/i.test(lower)) modifiers.paymentStatus = 'partial';
  if (/\boverdue\b/i.test(lower)) modifiers.paymentStatus = 'overdue';

  // Extract limit: "top 5", "show 10"
  const limitMatch = lower.match(/(?:top|show|first|last)\s+(\d+)/);
  if (limitMatch) modifiers.limit = parseInt(limitMatch[1], 10);

  return modifiers;
};

// ── Main parse function ─────────────────────────────────────────────────────

export const parseQuery = (query) => {
  if (!query || typeof query !== 'string' || query.trim().length < 2) {
    return { type: 'unknown', raw: query, confidence: 0 };
  }

  const trimmed = query.trim();
  const intentResult = detectIntent(trimmed);

  if (!intentResult) {
    return { type: 'unknown', raw: trimmed, confidence: 0 };
  }

  const { intent, score, all } = intentResult;
  const timeframe = extractTimeframe(trimmed);
  const therapy = extractTherapy(trimmed);
  const comparator = extractComparator(trimmed);
  const numbers = extractNumbers(trimmed);
  const modifiers = detectModifiers(trimmed);

  // Build filters based on intent
  const filters = {};

  if (comparator) {
    if (comparator.type === 'gt') filters.remaining_gt = comparator.value;
    else if (comparator.type === 'lt') filters.remaining_lt = comparator.value;
    else if (comparator.type === 'eq') filters.remaining_eq = comparator.value;
    else if (comparator.type === 'between') {
      filters.remaining_min = comparator.min;
      filters.remaining_max = comparator.max;
    }
  }

  if (timeframe) filters.timeframe = timeframe;
  if (therapy) filters.therapy = therapy;
  if (modifiers.status) filters.status = modifiers.status;
  if (modifiers.paymentStatus) filters.paymentStatus = modifiers.paymentStatus;
  if (modifiers.sort) filters.sort = modifiers.sort;
  if (modifiers.limit) filters.limit = modifiers.limit;

  // Confidence score: based on keyword match density
  const wordCount = trimmed.split(/\s+/).length;
  const rawConfidence = (score / Math.max(wordCount * 0.5, 1)) * 50;
  const confidence = Math.min(Math.round(rawConfidence + (therapy ? 15 : 0) + (timeframe ? 15 : 0) + (comparator ? 15 : 0)), 98);

  return {
    type: intent,
    filters,
    raw: trimmed,
    confidence: Math.max(confidence, 25),
    metadata: {
      timeframe,
      therapy,
      comparator,
      numbers,
      modifiers,
      allIntents: all,
    },
  };
};

// ── Category classifier ─────────────────────────────────────────────────────

const CATEGORY_MAP = {
  revenue: 'finance',
  invoice_filter: 'finance',
  expense: 'finance',
  salary: 'finance',
  pricing: 'finance',
  settlement: 'finance',
  patient: 'patient',
  waiting_list: 'patient',
  appointment: 'appointment',
  therapist: 'appointment',
  therapy: 'appointment',
  cancellation: 'appointment',
  services: 'appointment',
  session_types: 'appointment',
  attendance: 'analytics',
  report: 'analytics',
  trend: 'analytics',
  predictive: 'analytics',
  clinical: 'analytics',
  designation: 'settings',
  support: 'general',
  referrals: 'patient',
  wallet_credits: 'finance',
  guests: 'patient',
  group_sessions: 'appointment',
  leave: 'analytics',
  roles: 'settings',
};

export const classifyCategory = (type) => CATEGORY_MAP[type] || 'general';

export default parseQuery;
